import { type FC, useCallback, useMemo, useState } from 'react';
import { type MasonryGalleryImage } from '@/components/Work/Masonry';
import { GalleryRow } from '@/components/Blog/BlogComponents/GalleryRow';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Captions from 'yet-another-react-lightbox/plugins/captions';

import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';

type GalleryLayout = 'single' | 'double' | 'triple';

interface GalleryRowProps {
  layout: GalleryLayout;
  images: MasonryGalleryImage[];
}

interface BlogGalleryProps {
  heading?: string;
  caption?: string;
  rows: GalleryRowProps[];
  shapedImages?: boolean;
}

const isVideo = (image: MasonryGalleryImage) => {
  if (image.kind === 'video') return true;
  if (image.sources?.some((source) => source.type?.toLowerCase().includes('video'))) return true;
  if (typeof image.src === 'string') {
    const lowerSrc = image.src.toLowerCase();
    return lowerSrc.endsWith('.mp4') || lowerSrc.endsWith('.webm') || lowerSrc.endsWith('.mov');
  }
  return false;
};

export const BlogGallery: FC<BlogGalleryProps> = ({ heading, caption, rows, shapedImages = false }) => {
  if (!rows || rows.length === 0) return null;

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const slides = useMemo(() => {
    return rows
      .flatMap((row) => row.images)
      .filter((image) => !isVideo(image))
      .map((image) => ({
        src: typeof image.src === 'string' ? image.src : '',
        description: image.caption,
        alt: image.alt,
      }))
      .filter((slide) => Boolean(slide.src));
  }, [rows]);

  const handleImageClick = useCallback(
    (image: MasonryGalleryImage) => {
      if (isVideo(image) || slides.length === 0) return;
      const index = slides.findIndex((slide) => slide.src === image.src);
      if (index === -1) return;
      setLightboxIndex(index);
    },
    [slides],
  );

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  return (
    <section className="w-full py-12">
      <div className="mx-auto flex w-full max-w-[96rem] flex-col gap-6">
        {heading || caption ? (
          <header className="flex flex-col gap-3 border-b border-zinc-200 pb-4 font-montreal text-zinc-900 md:flex-row md:items-end md:justify-between dark:border-white/15 dark:text-white">
            {heading ? (
              <h4 className="text-[1.85rem] font-light tracking-tight text-zinc-900 dark:text-white">
                {heading}
              </h4>
            ) : (
              <span aria-hidden className="sr-only">Gallery</span>
            )}
            {caption ? (
              <p className="text-base text-zinc-600 md:max-w-sm md:text-right dark:text-white/75">{caption}</p>
            ) : null}
          </header>
        ) : null}

        <div className="flex flex-col gap-6">
          {rows.map((row, index) => {
            return (
              <GalleryRow
                key={`${row.layout}-${index}`}
                layout={row.layout}
                images={row.images}
                shapedImages={shapedImages}
                onImageClick={handleImageClick}
              />
            );
          })}
        </div>

        {!heading && caption ? (
          <p className="mt-2 text-sm text-white/60">{caption}</p>
        ) : null}
      </div>

      {slides.length > 0 && lightboxIndex !== null ? (
        <Lightbox
          open
          close={closeLightbox}
          slides={slides}
          index={lightboxIndex}
          plugins={[Zoom, Fullscreen, Captions]}
          captions={{ descriptionTextAlign: 'center' }}
          animation={{ fade: 300 }}
        />
      ) : null}
    </section>
  );
};

export default BlogGallery;
